import { useTranslation } from "react-i18next"
import { Check, PencilSimple, TrashSimple } from "@phosphor-icons/react"
import { DropdownMenuItem, DropdownMenuLabel } from "@/components/ui/dropdown-menu"
import { DateOnly } from "@/lib/date-only"
import { cn } from "@/lib/utils"
import { BodyMeasurementActions } from "@/modules/patients/detail/health-information/body-measurement/body-measurement-actions"
import { PatientBodyMeasurementsOutputFragment$data } from "@/modules/patients/services/__generated__/PatientBodyMeasurementsOutputFragment.graphql"

interface BodyMeasurementRowProps extends React.HTMLAttributes<HTMLDivElement> {
  isEditMode?: boolean
  isEditing?: boolean
  bodyMeasurement: PatientBodyMeasurementsOutputFragment$data[0]["node"]
  onEdit: () => void
  onDelete: () => void
  onVerify?: () => void
}

const BodyMeasurementRow = ({
  isEditMode,
  isEditing,
  onEdit,
  onDelete,
  onVerify,
  bodyMeasurement,
}: BodyMeasurementRowProps) => {
  const { t } = useTranslation()
  const { id, date, height, weight, bodyMassIndex } = bodyMeasurement

  return (
    <div
      id={id}
      data-state="false"
      data-testid={"body-measurement-row"}
      className={cn(
        "border-border relative grid h-10 cursor-pointer grid-cols-[90px_minmax(0,2fr)_minmax(0,2fr)_minmax(0,1fr)] items-center justify-center gap-4 px-4 py-2 text-sm",
        isEditMode && "grid-cols-[90px_minmax(0,2fr)_minmax(0,2fr)_minmax(0,1fr)_40px]",
        isEditing && "bg-primary-surface text-on-primary-surface",
      )}>
      <div data-testid={`date-cell`} className="truncate">
        {new DateOnly(date).toDisplayableString()}
      </div>
      <div data-testid="height-cell" className="truncate text-center">
        {height ?? "-"}
      </div>
      <div data-testid="weight-cell" className="truncate text-center">
        {weight}
      </div>
      <div data-testid="bmi-cell" className="truncate text-center">
        {bodyMassIndex ?? "-"}
      </div>
      {isEditMode && (
        <BodyMeasurementActions canVerify={!!onVerify}>
          <DropdownMenuItem
            data-testid="body-measurement-edit"
            onClick={onEdit}
            className="text-on-surface hover:bg-surface-hovered focus:bg-surface-hovered items-center gap-4 transition-all duration-150">
            <PencilSimple className="text-on-surface-subdued" />
            <DropdownMenuLabel className="flex-1 p-0 font-normal">{t("common.button.edit")}</DropdownMenuLabel>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={onDelete}
            data-testid="body-measurement-delete"
            className="text-on-surface hover:bg-surface-hovered focus:bg-surface-hovered items-center gap-4">
            <TrashSimple className="text-on-surface-subdued" />
            <DropdownMenuLabel className="flex-1 p-0 font-normal">{t("common.button.delete")}</DropdownMenuLabel>
          </DropdownMenuItem>

          {onVerify && (
            <DropdownMenuItem
              onClick={onVerify}
              data-testid="body-measurement-verify"
              className="text-on-surface hover:bg-surface-hovered focus:bg-surface-hovered items-center gap-4">
              <Check className="text-on-surface-subdued" />
              <DropdownMenuLabel className="flex max-w-40 flex-1 p-0 font-normal">
                <span className="truncate">{t("patient.healthInformation.bodyMeasurements.confirmPatientData")}</span>
              </DropdownMenuLabel>
            </DropdownMenuItem>
          )}
        </BodyMeasurementActions>
      )}
    </div>
  )
}

export { BodyMeasurementRow }
